import { css } from '@emotion/react';
import { cx, useSafeHandler } from './helpers';
import { Icon, IconSize } from './icons';
import { AuraTheme, useIcon } from './theme';

type FlashMessageFlavor = 'default' | 'success' | 'warning' | 'error';

const flavorToClassName: Record<FlashMessageFlavor, string> = {
  default: 'aura--flash-message-default',
  success: 'aura--flash-message-success',
  warning: 'aura--flash-message-warning',
  error: 'aura--flash-message-error',
};

const flavorToIcon: Record<FlashMessageFlavor, keyof AuraTheme['icons']> = {
  default: 'flashMessageDefault',
  success: 'flashMessageSuccess',
  warning: 'flashMessageWarning',
  error: 'flashMessageError',
};

type FlashMessageProps = {
  flavor: FlashMessageFlavor;
  message: string | JSX.Element | null;
  onDismiss?: () => void;
  className?: string;
};

export function FlashMessage({
  flavor,
  message,
  onDismiss,
  className,
}: FlashMessageProps): JSX.Element {
  const onDismissHandler = useSafeHandler(onDismiss);
  const icon = useIcon(flavorToIcon[flavor]);
  const closeIcon = useIcon('flashMessageClose');
  return (
    <div
      className={cx(
        'aura--flash-message',
        flavorToClassName[flavor],
        className,
      )}
    >
      <div
        className="aura--copy-sm-constant"
        css={css({
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 16,
        })}
      >
        <div className="aura--flash-message-icon">
          <Icon name={icon} size={IconSize.SMALL} />
        </div>
        <div>{message}</div>
      </div>
      {onDismissHandler && (
        <button
          className="aura--flash-message-close-button"
          onClick={onDismissHandler}
        >
          <Icon name={closeIcon} size={IconSize.SMALL} />
        </button>
      )}
    </div>
  );
}
